<template>
  <div class="dashboard-container d-flex">
    <!-- Sidebar -->
    <AdminSidebar :sidebarOpen="sidebarOpen" currentPage="dashboard" />

    <!-- Main Content Area -->
    <div
      class="main-content flex-grow-1"
      :class="{ 'sidebar-collapsed': !sidebarOpen }"
    >
      <!-- Navbar -->
      <AdminNavbar 
        :title="$t('dashboard.title')" 
        :userName="userName" 
        @toggleSidebar="toggleSidebar" 
        @logout="handleLogout" 
      />

      <!-- Content -->
      <div class="container-fluid p-4">
        <div class="row mb-3">
          <div class="col">
            <p class="text-muted">{{ $t('dashboard.welcomeMessage') }}</p>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="isLoading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="mt-3 text-muted">Loading dashboard data...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="alert alert-danger" role="alert">
          <strong>Error:</strong> {{ error }}
        </div>

        <!-- Stats Cards -->
        <div v-else class="row g-4">
          <!-- Total Employees -->
          <div class="col-md-6 col-lg-3">
            <div class="card stat-card border-0 shadow-sm h-100">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-start">
                  <div>
                    <p class="text-muted mb-1 small">{{ $t('dashboard.totalEmployees') }}</p>
                    <h3 class="fw-bold mb-0">{{ totalEmployees }}</h3>
                  </div>
                  <div class="stat-icon bg-primary bg-opacity-10 rounded-3 p-3">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="currentColor"
                      class="bi bi-people-fill text-primary"
                      viewBox="0 0 16 16"
                    >
                      <path
                        d="M7 14s-1 0-1-1 1-4 5-4 5 3 5 4-1 1-1 1H7zm4-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"
                      />
                      <path
                        fill-rule="evenodd"
                        d="M5.216 14A2.238 2.238 0 0 1 5 13c0-1.355.68-2.75 1.936-3.72A6.325 6.325 0 0 0 5 9c-4 0-5 3-5 4s1 1 1 1h4.216z"
                      />
                      <path d="M4.5 8a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- This Month Payroll -->
          <div class="col-md-6 col-lg-3">
            <div class="card stat-card border-0 shadow-sm h-100">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-start">
                  <div>
                    <p class="text-muted mb-1 small">{{ $t('dashboard.monthlyPayroll') }}</p>
                    <h3 class="fw-bold mb-0">
                      {{ formatCurrency(monthlyPayroll) }}
                    </h3>
                  </div>
                  <div class="stat-icon bg-success bg-opacity-10 rounded-3 p-3">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="currentColor"
                      class="bi bi-currency-euro text-success"
                      viewBox="0 0 16 16"
                    >
                      <path
                        d="M4 9.42h1.063C5.4 12.323 7.317 14 10.34 14c.622 0 1.167-.068 1.659-.185v-1.3c-.484.119-1.045.17-1.659.17-2.1 0-3.455-1.198-3.775-3.264h4.017v-.928H6.497v-.936c0-.11 0-.219.008-.329h4.078v-.927H6.618c.388-1.898 1.719-2.985 3.723-2.985.614 0 1.175.05 1.659.177V2.194A6.617 6.617 0 0 0 10.341 2c-3.023 0-4.941 1.676-5.277 4.42H4v.928h1.053v1.265H4v.927z"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Pending Timesheets -->
          <div class="col-md-6 col-lg-3">
            <div class="card stat-card border-0 shadow-sm h-100">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-start">
                  <div>
                    <p class="text-muted mb-1 small">{{ $t('dashboard.pendingTimesheets') }}</p>
                    <h3 class="fw-bold mb-0">{{ pendingTimesheets }}</h3>
                  </div>
                  <div class="stat-icon bg-warning bg-opacity-10 rounded-3 p-3">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="currentColor"
                      class="bi bi-clock-fill text-warning"
                      viewBox="0 0 16 16"
                    >
                      <path
                        d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Open Advances -->
          <div class="col-md-6 col-lg-3">
            <div class="card stat-card border-0 shadow-sm h-100">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-start">
                  <div>
                    <p class="text-muted mb-1 small">{{ $t('dashboard.pendingAdvances') }}</p>
                    <h3 class="fw-bold mb-0">{{ openAdvances }}</h3>
                  </div>
                  <div class="stat-icon bg-info bg-opacity-10 rounded-3 p-3">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="currentColor"
                      class="bi bi-wallet2 text-info"
                      viewBox="0 0 16 16"
                    >
                      <path
                        d="M12.136.326A1.5 1.5 0 0 1 14 1.78V3h.5A1.5 1.5 0 0 1 16 4.5v9a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 0 13.5v-9a1.5 1.5 0 0 1 1.432-1.499L12.136.326zM5.562 3H13V1.78a.5.5 0 0 0-.621-.484L5.562 3zM1.5 4a.5.5 0 0 0-.5.5v9a.5.5 0 0 0 .5.5h13a.5.5 0 0 0 .5-.5v-9a.5.5 0 0 0-.5-.5h-13z"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import AdminSidebar from "@/components/AdminSidebar.vue";
import AdminNavbar from "@/components/AdminNavbar.vue";

const API_URL = process.env.VUE_APP_API_URL || "http://localhost:4000";

export default {
  name: "AdminDashboard",
  components: {
    AdminSidebar,
    AdminNavbar
  },
  data() {
    return {
      totalEmployees: 0,
      monthlyPayroll: 0,
      pendingTimesheets: 0,
      openAdvances: 0,
      sidebarOpen: true,
      isLoading: false,
      error: null,
      userName: "",
    };
  },
  mounted() {
    this.loadUserInfo();
    this.fetchDashboardData();
  },
  methods: {
    loadUserInfo() {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      this.userName = user.name || "Admin";
    },
    async fetchDashboardData() {
      this.isLoading = true;
      this.error = null;

      try {
        const token = localStorage.getItem("token");
        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };

        // Fetch dashboard statistics
        const response = await axios.get(`${API_URL}/api/dashboard/stats`, config);

        if (response.data.success) {
          const data = response.data.data;
          this.totalEmployees = data.totalEmployees || 0;
          this.monthlyPayroll = data.monthlyPayroll || 0;
          this.pendingTimesheets = data.pendingTimesheets || 0;
          this.openAdvances = data.openAdvances || 0;
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        
        // If API not implemented yet, use placeholder data
        if (error.response?.status === 404) {
          this.totalEmployees = 247;
          this.monthlyPayroll = 185750;
          this.pendingTimesheets = 18;
          this.openAdvances = 5;
        } else {
          this.error = "Failed to load dashboard data. Please try again.";
        }
      } finally {
        this.isLoading = false;
      }
    },
    formatCurrency(amount) {
      return new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: "EUR",
      }).format(amount);
    },
    toggleSidebar() {
      this.sidebarOpen = !this.sidebarOpen;
    },
    handleLogout() {
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      this.$router.push("/");
    },
  },
};
</script>

<style scoped>
.dashboard-container {
  min-height: 100vh;
  background-color: #f8f9fa;
}

/* Sidebar Styles */
.sidebar {
  width: 280px;
  background-color: #2c3e50;
  color: white;
  min-height: 100vh;
  position: fixed;
  left: 0;
  top: 0;
  overflow-y: auto;
  transition: all 0.3s ease;
  z-index: 1000;
}

.sidebar.collapsed {
  width: 70px;
}

.sidebar-header {
  padding: 2rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  transition: padding 0.3s ease;
}

.sidebar.collapsed .sidebar-header {
  padding: 2rem 0.5rem;
}

.sidebar-header h4 {
  color: white;
  font-weight: 600;
  font-size: 1.5rem;
  white-space: nowrap;
  overflow: hidden;
}

.sidebar-nav {
  padding: 1rem 0;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.nav-item {
  display: flex;
  align-items: center;
  padding: 1rem 1.5rem;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 1.05rem;
  gap: 1rem;
  white-space: nowrap;
  overflow: hidden;
}

.sidebar.collapsed .nav-item {
  padding: 1rem 0.5rem;
  justify-content: center;
  font-size: 0;
}

.sidebar.collapsed .nav-item svg {
  margin: 0;
}

.nav-item:hover {
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
}

.nav-item.active {
  background-color: rgba(255, 255, 255, 0.15);
  color: white;
  border-left: 4px solid #667eea;
}

.nav-item svg {
  flex-shrink: 0;
}

/* Main Content */
.main-content {
  margin-left: 280px;
  min-height: 100vh;
  background-color: #f8f9fa;
  transition: margin-left 0.3s ease;
}

.main-content.sidebar-collapsed {
  margin-left: 70px;
}

.navbar {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.stat-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
}

.stat-icon {
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
}

h3 {
  font-size: 2rem;
}

.small {
  font-size: 0.875rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Responsive */
@media (max-width: 768px) {
  .sidebar {
    transform: translateX(-100%);
  }

  .sidebar:not(.collapsed) {
    transform: translateX(0);
  }

  .main-content {
    margin-left: 0 !important;
  }
}
</style>
